// Shared jotai state for the pricing pages. Reference data (enums, curves, contracts) is loaded
// once into module-global atoms and reused across the four pages — navigating between them reuses
// the cache, and a save on one page can refresh the shared list the others read.
import { useCallback } from 'react';
import { atom, useAtom, useSetAtom } from 'jotai';
import { pricingApi } from './api';
import type { Contract, Curve, PricingEnums } from './types';

export const enumsAtom = atom<PricingEnums | null>(null);
export const curvesAtom = atom<Curve[]>([]);
export const contractsAtom = atom<Contract[]>([]); // all contracts (user + canonical compositions)

// Load the backend enums once; subsequent callers reuse the cached atom value.
export function useEnums() {
  const [enums, setEnums] = useAtom(enumsAtom);
  const load = useCallback(() => {
    if (enums) return Promise.resolve(enums);
    return pricingApi.enums().then((data) => {
      setEnums(data);
      return data;
    });
  }, [enums, setEnums]);
  return { enums, load };
}

// Refresh the shared curve list; returns the fresh rows so callers can select into them.
export function useRefreshCurves() {
  const set = useSetAtom(curvesAtom);
  return useCallback(
    () =>
      pricingApi.listCurves().then((r) => {
        set(r.results);
        return r.results;
      }),
    [set]
  );
}

export function useRefreshContracts() {
  const set = useSetAtom(contractsAtom);
  return useCallback(
    () =>
      pricingApi.listContracts().then((r) => {
        set(r.results);
        return r.results;
      }),
    [set]
  );
}
